import React, { useState } from 'react';
import styled from 'styled-components';
import Input from '../UI/Input';
import InputCurrency from '../UI/InputCurrency';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { toast } from 'react-toastify';
import { updateVoucher } from '@/api/voucher';
import { useUIStore } from '@/store/useUIStore';
import { TableVouchers } from '@/pages/Admin/Vouchers';

interface Props {
   data: TableVouchers;
}

type ErrorState = {
   code: boolean;
   price: boolean;
};

const EditVoucher = ({ data }: Props) => {
   const { handleClose, handleRefresh } = useUIStore();
   const [isLoading, setIsLoading] = useState(false);

   const [values, setValues] = useState({
      code: data.code,
      price: String(data.price),
   });

   const [errors, setErrors] = useState<ErrorState>({
      code: false,
      price: false,
   });

   const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
   ) => {
      const { name, value } = e.target;
      setValues((prev) => ({ ...prev, [name]: value }));
      if (value.trim() !== '') {
         setErrors((prev) => ({ ...prev, [name]: false }));
      }
   };

   const validateForm = () => {
      const newErrors = {
         code: !values.code.trim(),
         price: !values.price,
      };
      setErrors(newErrors);
      return !Object.values(newErrors).some(Boolean);
   };

   const handleSubmit = async () => {
      if (isLoading) return;
      if (!validateForm()) return;

      try {
         setIsLoading(true);
         const res = await updateVoucher(data._id, {
            code: values.code,
            price: Number(values.price),
            store: data.storeId,
         } as any);

         toast.success(res.msg || 'Voucher updated', {
            position: 'top-right',
            autoClose: 2500,
         });
         handleRefresh();
         handleClose();
      } catch (err: any) {
         toast.error(
            err?.response?.data?.msg || 'Failed to update voucher',
            { position: 'top-right', autoClose: 3000 }
         );
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Wrapper>
         <Content>
            <Form>
               <Input
                  label="Voucher Code"
                  name="code"
                  value={values.code}
                  placeholder="e.g SMF000001"
                  onChange={handleChange}
                  error={errors.code}
               />
               <InputCurrency
                  label="Amount"
                  name="price"
                  value={values.price}
                  placeholder="0.00"
                  onChange={handleChange}
                  error={errors.price}
               />
               <Hint>
                  Edit the title and description for this price tier from the{' '}
                  <strong>Rates</strong> panel in the voucher list.
               </Hint>
            </Form>
            <BtnWrapper>
               <div onClick={() => handleClose()}>
                  <Btn title="Cancel" type="outline" size="small" />
               </div>
               <div onClick={handleSubmit}>
                  <Btn
                     title={isLoading ? 'Saving...' : 'Save'}
                     type={isLoading ? 'disabled' : 'normal'}
                     size="small"
                     bgcolor={isLoading ? colors.GRAY10 : colors.PRIMARY}
                     textcolor={isLoading ? colors.GRAY1 : colors.WHITE}
                     hasIcon={isLoading}
                     icon="loader"
                     iconPosition="right"
                  />
               </div>
            </BtnWrapper>
         </Content>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 500px;
   height: 100%;
   @media (max-width: 768px) { width: 100%; }
`;
const Form = styled.div`
   width: 100%;
   display: flex;
   flex-direction: column;
   gap: 1rem;
`;
const Content = styled.div`
   display: flex;
   flex-direction: column;
`;
const Hint = styled.p`
   font-size: 12px;
   color: ${colors.GRAY4};
   line-height: 1.5;
   strong { color: ${colors.PRIMARY}; }
`;
const BtnWrapper = styled.div`
   width: 100%;
   display: flex;
   justify-content: flex-end;
   gap: 1rem;
   margin-top: 1rem;
   @media (max-width: 768px) { padding-bottom: 5rem; }
`;

export default EditVoucher;
